import { config } from "@/config";
import type { RegistryData } from "@/services/context/register";
import {
    transformFirebaseDocToRegistryData,
    transformRegistryDataToFirebaseDoc,
} from "@/services/utils/firebase";
import * as Sentry from "@sentry/react";
import { type FirebaseError, initializeApp } from "firebase/app";
import {
    GoogleAuthProvider,
    createUserWithEmailAndPassword,
    deleteUser,
    getAdditionalUserInfo,
    getAuth,
    onAuthStateChanged,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
} from "firebase/auth";
import type { User } from "firebase/auth";
import { doc, getDoc, getFirestore, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";

const firebaseConfig = {
    apiKey: "AIzaSyCPuZek1oPQRh37rBIRrkaWtjjnuv9MVJ8",
    authDomain: "maifud-dde84.firebaseapp.com",
    projectId: "maifud-dde84",
    storageBucket: "maifud-dde84.appspot.com",
    messagingSenderId: "260188908096",
    appId: "1:260188908096:web:233ad452236ea3f6520da5",
    measurementId: "G-7ML9B4BH53",
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
    prompt: "select_account",
});

export const useUser = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);

    useEffect(() => {
        onAuthStateChanged(
            auth,
            (user) => {
                setUser(user);
                setIsLoading(false);
            },
            () => {
                throw new Error("Couldn´t get user status from firebase");
            },
            () => {
                setIsLoading(false);
            },
        );
    }, []);

    return {
        user,
        isLoading,
    };
};

export const getUser: () => Promise<User | null> = () => {
    return new Promise((resolve) => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
        });
    });
};

export const login = async (email: string, password: string) => {
    try {
        const userCredentials = await signInWithEmailAndPassword(
            auth,
            email,
            password,
        );
        return userCredentials.user;
    } catch (error) {
        throw Error((error as FirebaseError).message);
    }
};

const hasRegistryDocument = async (uid: string) => {
    const userDoc = await getDoc(doc(db, "users", uid));
    return userDoc.exists();
};

export const loginWithGoogle = async () => {
    try {
        const userCredentials = await signInWithPopup(auth, googleProvider);
        const registryExists = await hasRegistryDocument(
            userCredentials.user.uid,
        );

        if (!registryExists) {
            const additionalUserInfo = getAdditionalUserInfo(userCredentials);
            if (additionalUserInfo?.isNewUser) {
                await deleteUser(userCredentials.user);
            } else {
                await signOut(auth);
            }
            throw Error("auth/google-account-not-registered");
        }

        return userCredentials.user;
    } catch (error) {
        throw Error((error as FirebaseError | Error).message);
    }
};

export const logout = async () => {
    // Clear register localstorage on logout
    try {
        localStorage.setItem(config.REGISTER_LOCALSTORAGE_KEY, "");
    } catch (error) {
        Sentry.captureException(error);
    }
    return signOut(auth);
};

export const deleteUserFromFirebase = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
        return deleteUser(user)
            .then(() => {
                logout();
                location.href = config.BASE_URL;
            })
            .catch((error) => {
                throw new Error(error);
            });
    }
    throw new Error("User is required to be deleted");
};

const saveRegistryFormData = async (
    uid: string,
    registryData: RegistryData,
) => {
    const firebaseRegistryData = transformRegistryDataToFirebaseDoc(
        uid,
        registryData,
    );
    return setDoc(doc(db, "users", uid), firebaseRegistryData);
};

export const createUserWithGoogle = async ({
    registryData,
}: {
    registryData: RegistryData;
}) => {
    let shouldSignOutOnError = false;
    try {
        const userCredentials = await signInWithPopup(auth, googleProvider);
        const user = userCredentials.user;
        shouldSignOutOnError = true;
        const registryExists = await hasRegistryDocument(user.uid);

        if (registryExists) {
            throw Error("auth/google-account-already-registered");
        }

        if (!user.email) {
            throw Error("auth/google-email-required");
        }

        return saveRegistryFormData(user.uid, {
            ...registryData,
            email: user.email,
            display_name: registryData.display_name || user.displayName || "",
        });
    } catch (error) {
        if (shouldSignOutOnError) {
            await signOut(auth);
        }
        throw Error((error as FirebaseError | Error).message);
    }
};

export const createUser = async ({
    email,
    password,
    registryData,
}: {
    email: string;
    password: string;
    registryData: RegistryData;
}) => {
    return createUserWithEmailAndPassword(auth, email, password).then(
        (value) => {
            const uid = value.user.uid;
            return saveRegistryFormData(uid, registryData);
        },
    );
};

export const getRegistryUserData = async () => {
    const user = await getUser();
    if (user) {
        const document = await getDoc(doc(db, "users", user.uid));
        if (!document.exists()) {
            return {};
        }
        const transformedData = transformFirebaseDocToRegistryData(
            document.data(),
        );
        return transformedData;
    }
    return {};
};

export const resetPassword = async (email: string) => {
    return sendPasswordResetEmail(auth, email);
};
